/**
 * Toast Manager
 * 
 * Centralized toast notification manager with:
 * - Predefined messages from constants
 * - Consistent styling and behavior
 * - Type-safe message access
 * - Customizable options
 */

import { toast } from 'sonner';

import { ToastMessages } from './messages';
import { createToastOptions, getToastDuration, type ToastOptions } from './config';

/**
 * Toast Manager class
 * 
 * Provides a centralized interface for displaying toast notifications
 * with predefined messages and consistent styling.
 */
class ToastManager {
  /**
   * Display a success toast
   */
  success(message: string, description?: string, options?: Partial<ToastOptions>): void {
    toast.success(message, {
      description,
      duration: getToastDuration('success'),
      ...createToastOptions(options),
    } as Parameters<typeof toast.success>[1]);
  }

  /**
   * Display an error toast
   */
  error(message: string, description?: string, options?: Partial<ToastOptions>): void {
    toast.error(message, {
      description,
      duration: getToastDuration('error'),
      ...createToastOptions(options),
    } as Parameters<typeof toast.error>[1]);
  }

  /**
   * Display a warning toast
   */
  warning(message: string, description?: string, options?: Partial<ToastOptions>): void {
    toast.warning(message, {
      description,
      duration: getToastDuration('warning'),
      ...createToastOptions(options),
    } as Parameters<typeof toast.warning>[1]);
  }

  /**
   * Display an info toast
   */
  info(message: string, description?: string, options?: Partial<ToastOptions>): void {
    toast.info(message, {
      description,
      duration: getToastDuration('info'),
      ...createToastOptions(options),
    } as Parameters<typeof toast.info>[1]);
  }

  /**
   * Display a loading toast
   */
  loading(message: string, description?: string, options?: Partial<ToastOptions>): void {
    toast.loading(message, {
      description,
      ...createToastOptions(options),
    } as Parameters<typeof toast.loading>[1]);
  }

  /**
   * Display a promise toast (for async operations)
   * Note: toast.promise returns a promise-like object with unwrap(), 
   * so we return the original promise for consistency
   */
  promise<T>(
    promise: Promise<T>,
    messages: {
      loading: string;
      success: string | ((data: T) => string);
      error: string | ((error: Error) => string);
    },
    options?: Partial<ToastOptions>
  ): Promise<T> {
    toast.promise(promise, {
      loading: messages.loading,
      success: messages.success,
      error: messages.error,
      ...createToastOptions(options),
    } as Parameters<typeof toast.promise>[1]);
    return promise;
  }

  /**
   * Display a success toast with an undo action
   */
  successWithUndo(
    message: string,
    onUndo: () => void | Promise<void>,
    options?: Partial<ToastOptions>
  ): void {
    toast.success(message, {
      duration: 10000,
      action: {
        label: 'Undo',
        onClick: onUndo,
      },
      ...createToastOptions(options),
    } as Parameters<typeof toast.success>[1]);
  }

  /**
   * Dismiss a toast by ID
   */
  dismiss(toastId?: string | number): void {
    toast.dismiss(toastId);
  }

  /**
   * Dismiss all toasts
   */
  dismissAll(): void {
    toast.dismiss();
  }

  /**
   * Predefined messages - LinkedTable operations
   */
  linkedTable = {
    created: () => this.success(ToastMessages.LINKED_TABLE.CREATED),
    createdFailed: (error?: string) => this.error(ToastMessages.LINKED_TABLE.CREATED_FAILED(error)),
    saved: () => this.success(ToastMessages.LINKED_TABLE.SAVED),
    savedFailed: (error?: string) => this.error(ToastMessages.LINKED_TABLE.SAVED_FAILED(error)),
    nameRequired: () =>
      this.error(
        ToastMessages.LINKED_TABLE.NAME_REQUIRED,
        ToastMessages.LINKED_TABLE.NAME_REQUIRED_DESC
      ),
    deleted: (name: string) => this.success(ToastMessages.LINKED_TABLE.DELETED(name)),
    deleteFailed: (error?: string) => this.error(ToastMessages.LINKED_TABLE.DELETE_FAILED(error)),
    deleteBlocked: (linkedTables: string[]) =>
      this.error(
        ToastMessages.LINKED_TABLE.DELETE_BLOCKED,
        ToastMessages.LINKED_TABLE.DELETE_BLOCKED_DESC(linkedTables)
      ),
    previewFailed: () => this.error(ToastMessages.LINKED_TABLE.PREVIEW_FAILED),
    changesReverted: () => this.info(ToastMessages.LINKED_TABLE.CHANGES_REVERTED),
    relationshipRemoved: () => this.success(ToastMessages.LINKED_TABLE.RELATIONSHIP_REMOVED),
    relationshipRemovedWithDetails: (details: string[]) =>
      this.info(ToastMessages.LINKED_TABLE.RELATIONSHIP_REMOVED_WITH_DETAILS(details)),
  };

  /**
   * Predefined messages - Schema operations
   */
  schema = {
    fieldDeleted: () => this.success(ToastMessages.SCHEMA.FIELD_DELETED),
    fieldDeleteFailed: (error?: string) =>
      this.error(ToastMessages.SCHEMA.FIELD_DELETE_FAILED(error)),
    fieldDeleteBlocked: (linkedTables: string[]) =>
      this.error(
        ToastMessages.SCHEMA.FIELD_DELETE_BLOCKED,
        ToastMessages.SCHEMA.FIELD_DELETE_BLOCKED_DESC(linkedTables)
      ),
    validationFailed: (error?: string) =>
      this.error(ToastMessages.SCHEMA.VALIDATION_FAILED(error)),
    saved: () => this.success(ToastMessages.SCHEMA.SAVED),
    saveFailed: (error?: string) => this.error(ToastMessages.SCHEMA.SAVE_FAILED(error)),
  };

  /**
   * Predefined messages - Version operations
   */
  version = {
    created: () => this.success(ToastMessages.VERSION.CREATED, ToastMessages.VERSION.CREATED_DESC),
    validationError: (error?: string) =>
      this.error(ToastMessages.VERSION.VALIDATION_ERROR(error)),
    processingFailed: (errorMessage: string) =>
      this.error(ToastMessages.VERSION.PROCESSING_FAILED(errorMessage)),
    deleted: (fileName?: string) =>
      this.success(
        ToastMessages.VERSION.DELETED,
        fileName ? ToastMessages.VERSION.DELETED_DESC(fileName) : undefined
      ),
    deleteFailed: (error?: string) => this.error(ToastMessages.VERSION.DELETE_FAILED(error)),
    deleteBlocked: (linkedTables: string[]) =>
      this.error(
        ToastMessages.VERSION.DELETE_BLOCKED,
        ToastMessages.VERSION.DELETE_BLOCKED_DESC(linkedTables)
      ),
    deleteProtected: () =>
      this.warning(
        ToastMessages.VERSION.DELETE_PROTECTED,
        ToastMessages.VERSION.DELETE_PROTECTED_DESC
      ),
    deleteLast: () =>
      this.warning(ToastMessages.VERSION.DELETE_LAST, ToastMessages.VERSION.DELETE_LAST_DESC),
    // System tag operations
    setCurrent: () =>
      this.success(ToastMessages.VERSION.SET_CURRENT, ToastMessages.VERSION.SET_CURRENT_DESC),
    setCurrentFailed: (error?: string) =>
      this.error(ToastMessages.VERSION.SET_CURRENT_FAILED, error),
    setPrevious: () =>
      this.success(ToastMessages.VERSION.SET_PREVIOUS, ToastMessages.VERSION.SET_PREVIOUS_DESC),
    setPreviousFailed: (error?: string) =>
      this.error(ToastMessages.VERSION.SET_PREVIOUS_FAILED, error),
    alreadyPrevious: () => this.warning(ToastMessages.VERSION.ALREADY_PREVIOUS),
    tagCleared: () =>
      this.success(ToastMessages.VERSION.TAG_CLEARED, ToastMessages.VERSION.TAG_CLEARED_DESC),
    tagClearFailed: (error?: string) => this.error(ToastMessages.VERSION.TAG_CLEAR_FAILED, error),
    // Export
    exportStarted: (format: string) =>
      this.success(
        ToastMessages.VERSION.EXPORT_STARTED,
        ToastMessages.VERSION.EXPORT_STARTED_DESC(format)
      ),
    exportFailed: (error?: string) => this.error(ToastMessages.VERSION.EXPORT_FAILED(error)),
    error: (message: string) => this.error(ToastMessages.VERSION.ERROR(message)),
  };

  /**
   * Predefined messages - Table operations
   */
  table = {
    created: (name: string) => this.success(ToastMessages.TABLE.CREATED(name)),
    saved: () => this.success(ToastMessages.TABLE.SAVED),
    deleted: (name: string) => this.success(ToastMessages.TABLE.DELETED(name)),
    error: (message: string) => this.error(ToastMessages.TABLE.ERROR(message)),
    nameRequired: () =>
      this.error(ToastMessages.TABLE.NAME_REQUIRED, ToastMessages.TABLE.NAME_REQUIRED_DESC),
    descriptionRequired: () =>
      this.error(
        ToastMessages.TABLE.DESCRIPTION_REQUIRED,
        ToastMessages.TABLE.DESCRIPTION_REQUIRED_DESC
      ),
    updated: () => this.success(ToastMessages.TABLE.UPDATED),
    updateFailed: (message?: string) => this.error(ToastMessages.TABLE.UPDATE_FAILED(message)),
    deleteBlocked: (linkedTables: string[]) =>
      this.error(
        ToastMessages.TABLE.DELETE_BLOCKED,
        ToastMessages.TABLE.DELETE_BLOCKED_DESC(linkedTables)
      ),
    deleteFailed: (message?: string) => this.error(ToastMessages.TABLE.DELETE_FAILED(message)),
    draftDeleted: () => this.success(ToastMessages.TABLE.DRAFT_DELETED),
    statusUpdated: () => this.success(ToastMessages.TABLE.STATUS_UPDATED),
    statusUpdateFailed: () => this.error(ToastMessages.TABLE.STATUS_UPDATE_FAILED),
  };

  /**
   * Predefined messages - Notification operations
   */
  notification = {
    preferencesSaved: () =>
      this.success(
        ToastMessages.NOTIFICATION.PREFERENCES_SAVED,
        ToastMessages.NOTIFICATION.PREFERENCES_SAVED_DESC
      ),
  };

  /**
   * Predefined messages - Folder operations
   * Error methods take an optional description to show the API error message
   */
  folder = {
    created: () => this.success(ToastMessages.FOLDER.CREATED),
    createFailed: (description?: string) =>
      this.error(ToastMessages.FOLDER.CREATE_FAILED, description),
    updated: () => this.success(ToastMessages.FOLDER.UPDATED),
    updateFailed: (description?: string) =>
      this.error(ToastMessages.FOLDER.UPDATE_FAILED, description),
    renamed: () => this.success(ToastMessages.FOLDER.RENAMED),
    renameFailed: (description?: string) =>
      this.error(ToastMessages.FOLDER.RENAME_FAILED, description),
    deleted: () => this.success(ToastMessages.FOLDER.DELETED),
    deleteFailed: (description?: string) =>
      this.error(ToastMessages.FOLDER.DELETE_FAILED, description),
    restored: () => this.success(ToastMessages.FOLDER.RESTORED),
    restoreFailed: (description?: string) =>
      this.error(ToastMessages.FOLDER.RESTORE_FAILED, description),
    moved: (folderName: string) => this.success(ToastMessages.FOLDER.MOVED(folderName)),
    movedToRoot: () => this.success(ToastMessages.FOLDER.MOVED_TO_ROOT),
    moveFailed: (description?: string) =>
      this.error(ToastMessages.FOLDER.MOVE_FAILED, description),
    nameConflict: (name: string) => this.error(ToastMessages.FOLDER.NAME_CONFLICT(name)),
    cannotMoveIntoSelf: () => this.error(ToastMessages.FOLDER.CANNOT_MOVE_INTO_SELF),
    cannotMoveIntoChild: () => this.error(ToastMessages.FOLDER.CANNOT_MOVE_INTO_CHILD),
    parentNotFound: () => this.error(ToastMessages.FOLDER.PARENT_NOT_FOUND),
    emptyName: () => this.error(ToastMessages.FOLDER.EMPTY_NAME),
  };

  /**
   * Predefined messages - Workspace item operations
   * Error methods take an optional description to show the API error message
   */
  workspace = {
    // Trash operations
    itemTrashed: () => this.success(ToastMessages.WORKSPACE.ITEM_TRASHED),
    itemsTrashed: (count: number) => this.success(ToastMessages.WORKSPACE.ITEMS_TRASHED(count)),
    itemRestored: () => this.success(ToastMessages.WORKSPACE.ITEM_RESTORED),
    itemsRestored: (count: number) => this.success(ToastMessages.WORKSPACE.ITEMS_RESTORED(count)),
    restoreFailed: (description?: string) =>
      this.error(ToastMessages.WORKSPACE.RESTORE_FAILED, description),
    trashFailed: (description?: string) =>
      this.error(ToastMessages.WORKSPACE.TRASH_FAILED, description),
    trashEmptied: () => this.success(ToastMessages.WORKSPACE.TRASH_EMPTIED),
    trashEmptyFailed: (description?: string) =>
      this.error(ToastMessages.WORKSPACE.TRASH_EMPTY_FAILED, description),
    permanentDelete: () => this.success(ToastMessages.WORKSPACE.PERMANENT_DELETE),
    permanentDeleteFailed: (description?: string) =>
      this.error(ToastMessages.WORKSPACE.PERMANENT_DELETE_FAILED, description),

    // Move operations
    moveSuccess: (folderName: string) =>
      this.success(ToastMessages.WORKSPACE.MOVE_SUCCESS(folderName)),
    moveToRoot: () => this.success(ToastMessages.WORKSPACE.MOVE_TO_ROOT),
    moveFailed: (description?: string) =>
      this.error(ToastMessages.WORKSPACE.MOVE_FAILED, description),
    bulkMoveSuccess: (count: number, folderName: string) =>
      this.success(ToastMessages.WORKSPACE.BULK_MOVE_SUCCESS(count, folderName)),
    bulkMoveToRoot: (count: number) => this.success(ToastMessages.WORKSPACE.BULK_MOVE_TO_ROOT(count)),
    undoMoveFailed: (description?: string) =>
      this.error(ToastMessages.WORKSPACE.UNDO_MOVE_FAILED, description),

    // Favorite operations
    favorited: () => this.success(ToastMessages.WORKSPACE.FAVORITED),
    unfavorited: () => this.success(ToastMessages.WORKSPACE.UNFAVORITED),
    favoriteFailed: (description?: string) =>
      this.error(ToastMessages.WORKSPACE.FAVORITE_FAILED, description),

    // Rename operations
    renamed: () => this.success(ToastMessages.WORKSPACE.RENAMED),
    renameFailed: (description?: string) =>
      this.error(ToastMessages.WORKSPACE.RENAME_FAILED, description),
    nameConflict: (name: string, type: string) =>
      this.error(ToastMessages.WORKSPACE.NAME_CONFLICT(name, type)),
    emptyName: () => this.error(ToastMessages.WORKSPACE.EMPTY_NAME),

    // Duplicate operations
    duplicated: (name: string) => this.success(ToastMessages.WORKSPACE.DUPLICATED(name)),
    duplicateFailed: (description?: string) =>
      this.error(ToastMessages.WORKSPACE.DUPLICATE_FAILED(description)),
    duplicateNotSupported: (type: string) =>
      this.warning(ToastMessages.WORKSPACE.DUPLICATE_NOT_SUPPORTED(type)),
    duplicateTableHint: () => this.info(ToastMessages.WORKSPACE.DUPLICATE_TABLE_HINT),
    duplicateFolderHint: () => this.info(ToastMessages.WORKSPACE.DUPLICATE_FOLDER_HINT),

    // Clipboard operations
    cut: (count: number) => this.success(ToastMessages.WORKSPACE.CUT(count)),
    copied: (count: number) => this.success(ToastMessages.WORKSPACE.COPIED(count)),
    pasted: () => this.success(ToastMessages.WORKSPACE.PASTED),
    pasteFailed: (description?: string) =>
      this.error(ToastMessages.WORKSPACE.PASTE_FAILED, description),
  };

  /**
   * Predefined messages - Member operations
   */
  member = {
    roleUpdated: (role: string) =>
      this.success(ToastMessages.MEMBER.ROLE_UPDATED, ToastMessages.MEMBER.ROLE_UPDATED_DESC(role)),
    roleUpdateFailed: (error?: string) =>
      this.error(ToastMessages.MEMBER.ROLE_UPDATE_FAILED(error)),
    removed: () =>
      this.success(ToastMessages.MEMBER.REMOVED, ToastMessages.MEMBER.REMOVED_DESC),
    removeFailed: (error?: string) => this.error(ToastMessages.MEMBER.REMOVE_FAILED(error)),
  };

  /**
   * Generic error messages
   */
  generic = {
    operationFailed: (error?: string) =>
      this.error(ToastMessages.GENERIC.OPERATION_FAILED, error),
    unexpectedError: () => this.error(ToastMessages.GENERIC.UNEXPECTED_ERROR),
    networkError: () => this.error(ToastMessages.GENERIC.NETWORK_ERROR),
    permissionDenied: () => this.error(ToastMessages.GENERIC.PERMISSION_DENIED),
    copied: () => this.success(ToastMessages.GENERIC.COPIED),
  };

  /**
   * Predefined messages - Access/workspace selection operations
   */
  access = {
    workspaceFound: () =>
      this.success(ToastMessages.ACCESS.WORKSPACE_FOUND, ToastMessages.ACCESS.WORKSPACE_FOUND_DESC),
    noInvitations: () =>
      this.info(ToastMessages.ACCESS.NO_INVITATIONS, ToastMessages.ACCESS.NO_INVITATIONS_DESC),
    checkFailed: () => this.error('Error', ToastMessages.ACCESS.CHECK_FAILED),
    workspaceCreated: (name: string) =>
      this.success(
        ToastMessages.ACCESS.WORKSPACE_CREATED,
        ToastMessages.ACCESS.WORKSPACE_CREATED_DESC(name)
      ),
    workspaceExists: () =>
      this.info(ToastMessages.ACCESS.WORKSPACE_EXISTS, ToastMessages.ACCESS.WORKSPACE_EXISTS_DESC),
    createFailed: (error?: string) =>
      this.error('Error', error ?? ToastMessages.ACCESS.CREATE_FAILED),
    selectFailed: () => this.error('Error', ToastMessages.ACCESS.SELECT_FAILED),
  };

  /**
   * Predefined messages - Table operation messages (type conversion, constraint change, etc.)
   */
  operation = {
    typeConversionStarted: (fieldName: string, fromType: string, toType: string) =>
      this.success(
        ToastMessages.OPERATION.TYPE_CONVERSION_STARTED,
        ToastMessages.OPERATION.TYPE_CONVERSION_STARTED_DESC(fieldName, fromType, toType)
      ),
    typeConversionCompleted: (fieldName: string, toType: string) =>
      this.success(
        ToastMessages.OPERATION.TYPE_CONVERSION_COMPLETED,
        ToastMessages.OPERATION.TYPE_CONVERSION_COMPLETED_DESC(fieldName, toType)
      ),
    typeConversionFailed: (error?: string) =>
      this.error(ToastMessages.OPERATION.TYPE_CONVERSION_FAILED(error)),
    constraintChangeStarted: (fieldName: string, constraintType: string, toValue: boolean) =>
      this.success(
        ToastMessages.OPERATION.CONSTRAINT_CHANGE_STARTED,
        ToastMessages.OPERATION.CONSTRAINT_CHANGE_STARTED_DESC(fieldName, constraintType, toValue)
      ),
    constraintChangeCompleted: (fieldName: string, constraintType: string, toValue: boolean) =>
      this.success(
        ToastMessages.OPERATION.CONSTRAINT_CHANGE_COMPLETED,
        ToastMessages.OPERATION.CONSTRAINT_CHANGE_COMPLETED_DESC(fieldName, constraintType, toValue)
      ),
    constraintValidationFailed: (violationCount: number) =>
      this.error(
        ToastMessages.OPERATION.CONSTRAINT_VALIDATION_FAILED,
        ToastMessages.OPERATION.CONSTRAINT_VALIDATION_FAILED_DESC(violationCount)
      ),
    constraintChangeFailed: (error?: string) =>
      this.error(ToastMessages.OPERATION.CONSTRAINT_CHANGE_FAILED(error)),
    operationInProgress: () =>
      this.warning(
        ToastMessages.OPERATION.OPERATION_IN_PROGRESS,
        ToastMessages.OPERATION.OPERATION_IN_PROGRESS_DESC
      ),
    operationCancelled: () => this.success(ToastMessages.OPERATION.OPERATION_CANCELLED),
    cancelFailed: (error?: string) => this.error(ToastMessages.OPERATION.CANCEL_FAILED(error)),
  };
}

/**
 * Singleton instance of ToastManager
 */
export const toastManager = new ToastManager();

/**
 * Export for convenience
 */
export default toastManager;
